import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FcGoogle } from "react-icons/fc";
import InputField from "components/fields/InputField";
import Checkbox from "components/checkbox";
import { useAuth } from "contexts/AuthContext";

export default function SignInForm({
  initialUsername = "",
  onSwitchToRegister,
  onSwitchToForgot,
}) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState(initialUsername);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username, password);
      // Return to whatever protected page sent them here, if any. "/" resolves
      // to the first route this user can actually see.
      navigate(location.state?.from?.pathname || "/", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h4 className="mb-2.5 text-4xl font-bold text-navy-700 dark:text-white">
        Sign In
      </h4>
      <p className="mb-9 ml-1 text-base text-gray-600">
        Enter your username and password to sign in!
      </p>

      <div className="mb-6 flex h-[50px] w-full items-center justify-center gap-2 rounded-xl bg-lightPrimary hover:cursor-pointer dark:bg-navy-800">
        <div className="rounded-full text-xl">
          <FcGoogle />
        </div>
        <h5 className="text-sm font-medium text-navy-700 dark:text-white">
          Sign In with Google
        </h5>
      </div>

      <div className="mb-6 flex items-center gap-3">
        <div className="h-px w-full bg-gray-200 dark:bg-navy-700" />
        <p className="text-base text-gray-600 dark:text-white"> or </p>
        <div className="h-px w-full bg-gray-200 dark:bg-navy-700" />
      </div>

      <form onSubmit={handleSubmit}>
        <InputField
          variant="auth"
          extra="mb-3"
          label="Username*"
          placeholder="your username"
          id="username"
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <InputField
          variant="auth"
          extra="mb-3"
          label="Password*"
          placeholder="Min. 6 characters"
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <div className="mb-4 flex items-center justify-between px-2">
          <div className="flex items-center">
            <Checkbox />
            <p className="ml-2 text-sm font-medium text-navy-700 dark:text-white">
              Keep me logged In
            </p>
          </div>
          <button
            type="button"
            onClick={onSwitchToForgot}
            className="bg-transparent cursor-pointer border-none p-0 text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-white"
          >
            Forgot Password?
          </button>
        </div>

        {error && (
          <p className="mb-3 text-sm font-medium text-red-500">{error}</p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="linear mt-2 w-full rounded-xl bg-brand-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
        >
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </form>

      <div className="mt-4">
        <span className="text-sm font-medium text-navy-700 dark:text-gray-600">
          Not registered yet?
        </span>
        <button
          onClick={onSwitchToRegister}
          className="bg-transparent ml-1 cursor-pointer border-none p-0 text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-white"
        >
          Create an account
        </button>
      </div>
    </>
  );
}
